import React from 'react';

/**
 * Shared Question Renderer Component
 * 
 * Provides consistent question rendering between QuestionnaireViewer (interactive mode) 
 * and ImprovedQuestionnaireBuilderModal (preview mode)
 */
const QuestionRenderer = ({
  question,
  questionnaire,
  response = null,
  onResponseChange = null,
  onMultipleChoice = null,
  onVideoEnded = null,
  mode = 'interactive', // 'interactive' | 'preview'
  questionId = null,
  questionIndex = 0
}) => {
  // Helper function: Get text color style with consistent opacity handling for gray colors
  const getTextColorStyle = (isSecondary) => {
    if (isSecondary === undefined) isSecondary = false;
    const color = (questionnaire && questionnaire.theme_text_color) || 
                  (questionnaire && questionnaire.theme_color) || '#000000';
    
    // Check if the color is gray-ish (low saturation)
    const normalizedColor = color.startsWith('#') ? color : `#${color}`;
    let hex = normalizedColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate saturation to detect gray colors
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;
    
    // For gray colors (low saturation), use higher opacity for secondary text
    // For colored text, use lower opacity for secondary text
    const opacity = isSecondary ? (saturation < 0.1 ? 0.7 : 0.6) : 1;
    
    return { color, opacity };
  };

  // Helper function: Generate CSS classes for multiple choice display layout
  const getMultipleChoiceLayoutClasses = (displaySettings) => {
    const layoutType = (displaySettings && displaySettings.layout_type) || 'grid';
    const itemsPerRow = (displaySettings && displaySettings.items_per_row) || 3;
    
    if (layoutType === 'vertical') {
      return 'space-y-4';
    }
    
    if (layoutType === 'horizontal') {
      return 'space-x-4 flex flex-wrap';
    }
    
    // Grid layout
    const gridClasses = {
      1: 'grid grid-cols-1 gap-6',
      2: 'grid grid-cols-1 md:grid-cols-2 gap-6',
      3: 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6',
      4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6',
      5: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4',
      6: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4',
      7: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3',
      8: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3',
      9: 'grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2',
      10: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-10 gap-2'
    };
    
    return gridClasses[itemsPerRow] || gridClasses[3];
  };

  // Helper function: Generate CSS classes for individual option container
  const getOptionContainerClasses = (displaySettings) => {
    const layoutType = (displaySettings && displaySettings.layout_type) || 'grid';
    
    if (layoutType === 'horizontal') {
      return 'space-x-3 flex items-center';
    }
    
    // Both 'grid' and 'vertical' use vertical spacing within each option
    return 'space-y-3';
  };

  // Helper function: Generate CSS classes for option images based on display settings
  const getImageClasses = (displaySettings) => {
    const layoutType = (displaySettings && displaySettings.layout_type) || 'grid';
    const itemsPerRow = (displaySettings && displaySettings.items_per_row) || 3;
    
    const baseClasses = 'object-cover rounded-lg shadow-sm border-2 cursor-pointer';
    
    if (layoutType === 'horizontal') {
      return `w-24 h-18 ${baseClasses}`; // Smaller for horizontal layout
    }
    
    if (layoutType === 'vertical') {
      return `w-full h-48 md:h-56 ${baseClasses}`; // Full width for vertical layout
    }
    
    // Grid layout - responsive sizing based on items per row
    const sizeClasses = {
      1: 'w-full h-64 md:h-72 lg:h-80', // Large for 1 column
      2: 'w-full h-56 md:h-64 lg:h-72', // Medium-large for 2 columns
      3: 'w-full h-48 md:h-56 lg:h-64', // Medium for 3 columns
      4: 'w-full h-40 md:h-48 lg:h-56'  // Smaller for 4 columns
    };
    
    return `${sizeClasses[itemsPerRow] || sizeClasses[3]} ${baseClasses}`;
  };

  // Event handlers with mode awareness
  const handleResponseChange = (qId, value, type = 'text') => {
    if (mode === 'preview' || !onResponseChange) return;
    onResponseChange(qId, value, type);
  };

  const handleMultipleChoice = (qId, optionId, allowMultiple = false) => {
    if (mode === 'preview' || !onMultipleChoice) return;
    onMultipleChoice(qId, optionId, allowMultiple);
  };

  const handleVideoEnded = () => {
    if (mode === 'preview' || !onVideoEnded) return;
    onVideoEnded();
  };

  // Use question ID or fall back to question index if ID is missing
  const effectiveQuestionId = questionId || question.id || question.uuid || questionIndex;
  
  // Get the saved response for this specific question
  const effectiveResponse = response || null;

  // Render based on question type
  switch (question.type) {
    case 'text':
      const textValue = (effectiveResponse && effectiveResponse.value) || '';
      
      return (
        <React.Fragment>
          <input
            key={`text-${effectiveQuestionId}-${questionIndex}`}
            type="text"
            value={textValue}
            onChange={(e) => handleResponseChange(effectiveQuestionId, e.target.value, 'text')}
            placeholder={mode === 'preview' ? 'Text input preview' : 'Type your answer here...'}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            readOnly={mode === 'preview'}
            className={`w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 questionnaire-input ${mode === 'preview' ? 'cursor-default' : ''}`}
            style={{ 
              color: getTextColorStyle().color,
              borderBottomColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af'
            }}
          />
          
          {/* Helper Text for Text Questions */}
          {mode === 'interactive' && (
            <p className="text-sm mt-2 text-left p-0 m-0" 
               style={getTextColorStyle(true)}
            >
              Shift ⌥ + Enter ↵ to make a line break
            </p>
          )}
        </React.Fragment>
      );

    case 'text_only':
      return (
        <div className="text-center text-gray-500 italic py-8">
          {mode === 'preview' ? 'Text-only display page (no input required)' : ''}
        </div>
      );

    case 'textarea':
      return (
        <React.Fragment>
          <textarea
            key={`textarea-${effectiveQuestionId}-${questionIndex}`}
            value={(effectiveResponse && effectiveResponse.value) || ''}
            onChange={(e) => handleResponseChange(effectiveQuestionId, e.target.value, 'textarea')}
            rows={4}
            placeholder={mode === 'preview' ? 'Multi-line text input preview' : 'Type your answer here...'}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            readOnly={mode === 'preview'}
            className={`w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 resize-none questionnaire-input ${mode === 'preview' ? 'cursor-default' : ''}`}
            style={{ 
              color: getTextColorStyle().color,
              borderBottomColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af'
            }}
          />
          
          {/* Helper Text for Textarea Questions */}
          {mode === 'interactive' && (
            <p className="text-sm mt-2 text-left p-0 m-0" 
               style={getTextColorStyle(true)}
            >
              Shift ⌥ + Enter ↵ to make a line break
            </p>
          )}
        </React.Fragment>
      );

    case 'yes_no':
      return (
        <div className="space-y-3">
          <div className="flex items-center space-x-3 justify-center">
            <input
              key={`yes-${effectiveQuestionId}-${questionIndex}`}
              type="radio"
              id={`yes-option-${questionIndex}-${mode}`}
              name={`question-${question.id}-${questionIndex}-${mode}`}
              value="yes"
              checked={(effectiveResponse && effectiveResponse.value) === 'yes'}
              onChange={() => handleResponseChange(effectiveQuestionId, 'yes', 'yes_no')}
              autoComplete="off"
              disabled={mode === 'preview'}
              className="h-4 w-4 border-0 focus:ring-0 outline-0"
              style={{ 
                accentColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af',
                outline: 'none',
                border: 'none'
              }}
            />
            <label 
              htmlFor={`yes-option-${questionIndex}-${mode}`} 
              className={`text-lg ${mode === 'preview' ? 'cursor-default' : 'cursor-pointer'}`}
              style={getTextColorStyle()}
            >
              Yes
            </label>
          </div>
          <div className="flex items-center space-x-3 justify-center">
            <input
              key={`no-${effectiveQuestionId}-${questionIndex}`}
              type="radio"
              id={`no-option-${questionIndex}-${mode}`}
              name={`question-${question.id}-${questionIndex}-${mode}`}
              value="no"
              checked={(effectiveResponse && effectiveResponse.value) === 'no'}
              onChange={() => handleResponseChange(effectiveQuestionId, 'no', 'yes_no')}
              autoComplete="off"
              disabled={mode === 'preview'}
              className="h-4 w-4 border-0 focus:ring-0 outline-0"
              style={{ 
                accentColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af',
                outline: 'none',
                border: 'none'
              }}
            />
            <label 
              htmlFor={`no-option-${questionIndex}-${mode}`} 
              className={`text-lg ${mode === 'preview' ? 'cursor-default' : 'cursor-pointer'}`}
              style={getTextColorStyle()}
            >
              No
            </label>
          </div>
        </div>
      );

    case 'multiple_choice':
      return (
        <div className={getMultipleChoiceLayoutClasses(question.display_settings)}>
          {(question.options || []).map((option, optionIndex) => (
            <div key={option.id || optionIndex} className={getOptionContainerClasses(question.display_settings)}>
              {/* Radio button and text - alignment based on display settings */}
              <div className="flex items-center space-x-3">
                <input
                  key={`option-${option.id}-${questionIndex}-${mode}`}
                  type="radio"
                  id={`option-${option.id}-${questionIndex}-${mode}`}
                  name={`question-${question.id}-${questionIndex}-${mode}`}
                  value={option.id}
                  checked={(effectiveResponse && effectiveResponse.value) === option.id}
                  onChange={() => handleMultipleChoice(effectiveQuestionId, option.id, false)}
                  autoComplete="off"
                  disabled={mode === 'preview'}
                  className="h-4 w-4 border-0 focus:ring-0 outline-0 flex-shrink-0"
                  style={{ 
                    accentColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af',
                    outline: 'none',
                    border: 'none'
                  }}
                />
                <label 
                  htmlFor={`option-${option.id}-${questionIndex}-${mode}`} 
                  className={`text-lg flex-grow ${mode === 'preview' ? 'cursor-default' : 'cursor-pointer'}`}
                  style={getTextColorStyle()}
                >
                  {option.option_text || option.text}
                </label>
              </div>
              {/* Show option image below text if available - full width */}
              {(option.image_url || option.media_url || option.video_url) && (
                <div className="w-full">
                  {/* Check if option has video */}
                  {(option.media_url || option.video_url) && 
                  ((option.media_url && option.media_url.includes('.mp4')) || (option.media_url && option.media_url.includes('.webm')) || 
                   (option.media_url && option.media_url.includes('.mov')) || option.video_url) ? (
                    <video 
                      controls={mode === 'interactive'}
                      className={getImageClasses(question.display_settings)}
                      style={{ borderColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af' }}
                      onError={(e) => {
                        console.error('Failed to load option video:', option.media_url || option.video_url)
                      }}
                      onEnded={handleVideoEnded}
                    >
                      <source src={option.media_url || option.video_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img 
                      src={option.image_url || option.media_url}
                      alt={`Option ${option.option_text || option.text || 'image'}`}
                      className={getImageClasses(question.display_settings)}
                      style={{ borderColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af' }}
                      onClick={() => mode === 'interactive' && handleMultipleChoice(effectiveQuestionId, option.id, false)}
                      onError={(e) => {
                        console.error('Failed to load option image:', option.image_url || option.media_url)
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      );

    case 'rating':
      const ratingOptions = (question.options && question.options.length) > 0 ? question.options : [
        { id: 1, option_text: 'Excellent' },
        { id: 2, option_text: 'Good' },
        { id: 3, option_text: 'Average' },
        { id: 4, option_text: 'Poor' },
        { id: 5, option_text: 'Very Poor' }
      ];
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {ratingOptions.map((option, optionIndex) => (
            <div key={option.id || optionIndex} className="space-y-3">
              {/* Radio button and text - left aligned */}
              <div className="flex items-center space-x-3">
                <input
                  key={`rating-${option.id}-${questionIndex}-${mode}`}
                  type="radio"
                  id={`rating-${option.id}-${questionIndex}-${mode}`}
                  name={`rating-${question.id}-${questionIndex}-${mode}`}
                  value={option.id}
                  checked={(effectiveResponse && effectiveResponse.value) === option.id}
                  onChange={() => handleResponseChange(effectiveQuestionId, option.id, 'rating')}
                  autoComplete="off"
                  disabled={mode === 'preview'}
                  className="h-4 w-4 border-0 focus:ring-0 outline-0 flex-shrink-0"
                  style={{ 
                    accentColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af',
                    outline: 'none',
                    border: 'none'
                  }}
                />
                <label 
                  htmlFor={`rating-${option.id}-${questionIndex}-${mode}`} 
                  className={`text-lg flex-grow ${mode === 'preview' ? 'cursor-default' : 'cursor-pointer'}`}
                  style={getTextColorStyle()}
                >
                  {option.option_text || option.text}
                </label>
              </div>
              {/* Show rating option image below text if available - full width */}
              {(option.image_url || option.media_url || option.video_url) && (
                <div className="w-full">
                  {/* Check if rating option has video */}
                  {(option.media_url || option.video_url) && 
                  ((option.media_url && option.media_url.includes('.mp4')) || (option.media_url && option.media_url.includes('.webm')) || 
                   (option.media_url && option.media_url.includes('.mov')) || option.video_url) ? (
                    <video 
                      controls={mode === 'interactive'}
                      className="w-full h-48 md:h-56 lg:h-64 object-cover rounded-lg shadow-sm border-2"
                      style={{ borderColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af' }}
                      onError={(e) => {
                        console.error('Failed to load rating option video:', option.media_url || option.video_url)
                      }}
                      onEnded={handleVideoEnded}
                    >
                      <source src={option.media_url || option.video_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img 
                      src={option.image_url || option.media_url}
                      alt={`Rating ${option.option_text || option.text || 'image'}`}
                      className={`w-full h-48 md:h-56 lg:h-64 object-cover rounded-lg shadow-sm border-2 ${mode === 'preview' ? '' : 'cursor-pointer'}`}
                      style={{ borderColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af' }}
                      onClick={() => mode === 'interactive' && handleResponseChange(effectiveQuestionId, option.id, 'rating')}
                      onError={(e) => {
                        console.error('Failed to load rating option image:', option.image_url || option.media_url)
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      );

    case 'image_only':
    case 'video_only':
      /* No input fields for image-only and video-only question types */
      return mode === 'preview' ? (
        <div className="text-center text-gray-500 italic py-8">
          {question.type === 'image_only' ? 'Image-only display' : 'Video-only display'} (no input required)
        </div>
      ) : null;

    default:
      /* Default Text Input for Unknown Types */
      return (
        <React.Fragment>
          <input
            key={`default-${effectiveQuestionId}-${questionIndex}`}
            type="text"
            value={(effectiveResponse && effectiveResponse.value) || ''}
            onChange={(e) => handleResponseChange(effectiveQuestionId, e.target.value, 'text')}
            placeholder={mode === 'preview' ? 'Default text input preview' : 'Type your answer here...'}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            readOnly={mode === 'preview'}
            className={`w-full text-lg bg-transparent border-0 border-b-2 focus:outline-none focus:ring-0 transition-colors text-left p-0 pb-2 m-0 questionnaire-input ${mode === 'preview' ? 'cursor-default' : ''}`}
            style={{ 
              color: getTextColorStyle().color,
              borderBottomColor: (questionnaire && questionnaire.theme_button_color) || '#1e40af'
            }}
          />
          
          {/* Helper Text for Text Questions */}
          {mode === 'interactive' && (
            <p className="text-sm mt-2 text-left p-0 m-0" 
               style={getTextColorStyle(true)}
            >
              Shift ⌥ + Enter ↵ to make a line break
            </p>
          )}
        </React.Fragment>
      );
  }
};

export default QuestionRenderer;